<script lang="ts" setup>
import { ref, toRefs } from 'vue';
import Calendar from 'primevue/calendar';
import ColumnGroup from 'primevue/columngroup';
import Row from 'primevue/row';
import { useUtilizationStore } from '@/store';
import { useDateTime } from '@/composables';
import { ClinicianInterface, SOWReportingTableInterface } from '@/types';
import ServiceProviderTimeUsageModal from './ServiceProviderTimeUsageModal.vue';

interface SOWReportingPropsInterface {
  sowData: SOWReportingTableInterface[];
}

const props = defineProps<SOWReportingPropsInterface>();
const { sowData } = toRefs(props);
const utilizationStore = useUtilizationStore();
const serviceProviderModalOpen = ref<boolean>(false);
const filterDate = ref<Date>(useDateTime().getMostRecentSunday());
const maxDate = ref<Date>(useDateTime().getMostRecentSunday());
const minDate = ref<Date>(new Date('2024-01-02'));
const selectedServiceProvider = ref<ClinicianInterface>();

const onDateSelect = async (value: Date) => {
  filterDate.value = value;
  await utilizationStore.updateSOWFilterDate(filterDate.value);
};

const openServiceProviderUtilizationModal = (serviceProvider: ClinicianInterface) => {
  selectedServiceProvider.value = serviceProvider;
  serviceProviderModalOpen.value = true;
};

const closeServiceProviderUtilizationModal = () => {
  serviceProviderModalOpen.value = false;
  selectedServiceProvider.value = undefined;
};

const displayNameSort = ({ clinician }) => clinician.display_name;

const exportCSV = async () => {
  await utilizationStore.getSOWReportingExportData();
  const headers = Object.keys(utilizationStore.sOWExportData[0] || {});
  const csvContent = `data:text/csv;charset=utf-8,${headers.join(',') + '\n'}${utilizationStore.sOWExportData
    .map((item) => Object.values(item).join(','))
    .join('\n')}`;

  const encodedUri = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `SOW_Reporting_Download_${useDateTime().generateTimestamp()}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<template>
  <Card class="flex-none flex flex-column">
    <template #header>
      <div class="flex justify-content-between w-full">
        <div class="inline-flex align-items-center">
          <span class="material-icons text-color-blue mr-2">query_stats</span>
          <h2>SOW Reporting</h2>
        </div>
        <div style="text-align: left">
          <AppButton type="secondary" text="CSV" icon-left="system_update_alt" @click="exportCSV" />
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex flex-wrap gap-3">
        <div class="flex pt-4">
          <h2 v-text="'SOW Reporting up to: '" />
        </div>
        <div class="flex">
          <Calendar
            v-model="filterDate"
            show-icon
            :disabled-days="[1, 2, 3, 4, 5, 6]"
            icon-display="input"
            class="mt-3 text-sm"
            date-format="MM dd, yy"
            :max-date="maxDate"
            :min-date="minDate"
            @date-select="onDateSelect"
          />
        </div>
      </div>
      <div class="p-3">
        <DataTable scrollable scroll-height="350px" :value="sowData" sort-field="billed_hours_percent" :sort-order="-1">
          <Column
            field="clinician"
            sortable
            :sort-field="displayNameSort"
            header-style="flex: 0 1 28%"
            body-style="flex: 0 1 28%"
            header="Service Provider"
          >
            <template #body="{ data }">
              <a
                class="font-semibold"
                @click="openServiceProviderUtilizationModal(data.clinician)"
                v-text="data.clinician.display_name"
              />
            </template>
          </Column>
          <Column
            field="organization"
            header-style="flex: 0 1 41%"
            body-style="flex: 0 1 41%"
            sortable
            header="Organization"
          ></Column>
          <Column
            field="billed_hours_diff"
            header="Hrs +/-"
            sortable
            header-class="justify-content-center"
            body-class="flex-auto justify-content-center"
          />
          <Column
            field="billed_hours_percent"
            sortable
            header-class="relative justify-content-center"
            body-class="percentage flex-auto justify-content-center"
          >
            <template #header>
              <span>%</span>
              <Icon
                v-tooltip.top="'Comparison of Total Billed Hours to SOW Hrs/Wk x Weeks'"
                name="info"
                class="absolute right-0 top-0 mt-2 text-gray-400 text-base cursor-pointer"
              />
            </template>
          </Column>
          <ColumnGroup type="footer">
            <Row>
              <Column footer-class="text-right" footer="Totals:" :colspan="2" />
              <Column
                footer-class="text-center"
                :footer="utilizationStore?.sOWTableTotals?.total_billed_hours_diff || 'N/A'"
              />
              <Column
                footer-class="percentage text-center"
                :footer="utilizationStore?.sOWTableTotals?.total_billed_hours_percent || '0'"
              />
            </Row>
          </ColumnGroup>
          <template #empty>No data to display</template>
        </DataTable>
        <ServiceProviderTimeUsageModal
          v-if="serviceProviderModalOpen"
          :service-provider="selectedServiceProvider as ClinicianInterface"
          :filter-date="filterDate"
          report-type="sow"
          @cancel="closeServiceProviderUtilizationModal"
        />
      </div>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card) {
  .p-card-content {
    padding: 0 !important;
  }
}

:deep(.p-calendar) {
  .pi.pi-calendar.p-button-icon {
    color: $white;
  }
}

:deep(.p-datatable) {
  .percentage::after {
    content: '%';
  }
}
</style>
